import { MatterRowContextMenu } from "@/components/features/matters/MatterRowContextMenu";
import { ClaimTypesCell } from "@/components/features/matters/ClaimTypesCell";
import {
  REPORT_CLASSIFICATION_COLUMN_ALIASES,
  REPORT_CLASSIFICATION_COLUMN_ANCHORS,
} from "@/lib/utils/reports/report-classification-columns";
import { IncidentTypeFilter } from "@/components/features/reports/IncidentTypeFilter";
import { useReportClassificationLabels } from "@/components/features/reports/use-report-classification-labels";
import { ClaimTypeFilter } from "@/components/features/reports/ClaimTypeFilter";
import { ReportUserFilter } from "@/components/features/reports/ReportUserFilter";
import { ReportFilterBar } from "@/components/features/reports/ReportFilterBar";
import { useUserDirectory } from "@/hooks/use-user-directory";
import { useUser } from "@/components/UserProvider";
import { ReportsPageLayout } from "@/components/features/reports/ReportsPageLayout";
import {
  DataTable,
  DataTableColumnHeader,
  DateFormField,
  Form,
  GlobalNotAuthorized,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import { MatterStatusSelectItems } from "@/components/ui/FormFields/MatterStatusSelectItems";
import {
  getUsersOptions,
  retrieveMatterSettingsQueryOptions,
  retrieveReportsMatterRecordRequestsReportQueryOptions,
} from "@/lib/data/react-query";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { MEDICAL_TREATMENT_RECORD_STATUS_OPTIONS } from "@/lib/data/static/matterStatics";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type ReportsMatterRecordRequestsReport } from "@/lib/types";
import { formatDate } from "@/lib/utils/format";
import {
  REPORT_DATE_RANGE_OPTIONS,
  REPORT_DATE_RANGE_SEARCH_VALUES,
  type ReportDateRangeType,
  buildMatterStatusLabelOptions,
  formatResolvedReportDateRangeLabel,
  isReportDateRangeType,
  resolveReportDateRange,
} from "@/lib/utils/reports/report-filter-options";
import { MultiValueCell } from "@/lib/utils/tableUtils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export const Route = createFileRoute("/_auth/reports/record-requests")({
  component: RouteComponent,
  validateSearch: z.object({
    assignee: z.string().optional(),
    dateRangeEnd: z.string().optional(),
    dateRangeStart: z.string().optional(),
    dateRangeType: z.enum(REPORT_DATE_RANGE_SEARCH_VALUES).optional(),
    medicalTreatmentRecordStatus: z.string().optional(),
    matterStatus: z.string().optional(),
    team: z.string().optional(),
    // Preserve older bookmarked label substring filters. New selections use incidentType IDs.
    type: z.string().optional(),
    incidentType: z.string().optional(),
    claimType: z.string().optional(),
  }),
});

const DEFAULT_DATE_RANGE_TYPE: ReportDateRangeType = "YEAR_TO_DATE";
const RECORD_REQUESTS_REPORT_TABLE_NAME =
  tanstackTableNames.reportsMatterRecordRequests;

const parseSearchValues = (value?: string) =>
  value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const serializeSearchValues = (values: string[]) =>
  values.length > 0 ? values.join(",") : undefined;

const addUniqueValue = (values: string[], value: string) =>
  values.includes(value) ? values : [...values, value];

const getUniqueTrimmedValues = (values: Array<string | null | undefined>) =>
  Array.from(
    new Set(values.map((value) => value?.trim() ?? "").filter(Boolean)),
  ).sort((left, right) => left.localeCompare(right));

const formatDateValue = (value?: string | null) =>
  formatDate(value ?? undefined) ?? "-";

const resolveOptionLabel = (
  options: ReadonlyArray<{ label: string; value: string }>,
  value: string,
) => options.find((option) => option.value === value)?.label ?? value;

function RouteComponent() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { hasPagePermission } = useUser();

  const canViewReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsRecordRequests,
  );
  const canViewMatterDetails = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetail,
  );

  const selectedDateRange: ReportDateRangeType =
    search.dateRangeType ?? DEFAULT_DATE_RANGE_TYPE;
  const dateRangeStart = search.dateRangeStart;
  const dateRangeEnd = search.dateRangeEnd;
  const dateRangeForm = useForm<{
    dateRangeEnd: string;
    dateRangeStart: string;
  }>({
    defaultValues: {
      dateRangeEnd: dateRangeEnd ?? "",
      dateRangeStart: dateRangeStart ?? "",
    },
  });
  const watchedDateRangeStart = dateRangeForm.watch("dateRangeStart");
  const watchedDateRangeEnd = dateRangeForm.watch("dateRangeEnd");

  const [selectedTeams, setSelectedTeams] = useState<string[]>(() =>
    parseSearchValues(search.team),
  );
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>(() =>
    parseSearchValues(search.assignee),
  );
  const [selectedMatterStatuses, setSelectedMatterStatuses] = useState<
    string[]
  >(() => parseSearchValues(search.matterStatus));
  const selectedClaimTypeIds = useMemo(
    () => parseSearchValues(search.claimType),
    [search.claimType],
  );
  const selectedLegacyIncidentTypeLabels = useMemo(
    () => parseSearchValues(search.type),
    [search.type],
  );
  const selectedIncidentTypeIds = useMemo(
    () => parseSearchValues(search.incidentType),
    [search.incidentType],
  );
  const classificationFilters = useReportClassificationLabels(
    selectedIncidentTypeIds,
    selectedClaimTypeIds,
    canViewReport,
  );
  const [
    selectedMedicalTreatmentRecordStatuses,
    setSelectedMedicalTreatmentRecordStatuses,
  ] = useState<string[]>(() =>
    parseSearchValues(search.medicalTreatmentRecordStatus),
  );

  const [teamSelectValue, setTeamSelectValue] = useState<string | undefined>();
  const [matterStatusSelectValue, setMatterStatusSelectValue] = useState<
    string | undefined
  >();
  const [
    medicalTreatmentRecordStatusSelectValue,
    setMedicalTreatmentRecordStatusSelectValue,
  ] = useState<string | undefined>();

  useEffect(() => {
    setSelectedTeams(parseSearchValues(search.team));
  }, [search.team]);
  useEffect(() => {
    setSelectedAssignees(parseSearchValues(search.assignee));
  }, [search.assignee]);
  useEffect(() => {
    setSelectedMatterStatuses(parseSearchValues(search.matterStatus));
  }, [search.matterStatus]);
  useEffect(() => {
    setSelectedMedicalTreatmentRecordStatuses(
      parseSearchValues(search.medicalTreatmentRecordStatus),
    );
  }, [search.medicalTreatmentRecordStatus]);
  useEffect(() => {
    if (selectedDateRange !== "CUSTOM_DATE_RANGE") {
      return;
    }
    if (dateRangeStart || dateRangeEnd) {
      return;
    }
    dateRangeForm.reset(
      { dateRangeEnd: "", dateRangeStart: "" },
      { keepDefaultValues: true },
    );
  }, [dateRangeEnd, dateRangeForm, dateRangeStart, selectedDateRange]);
  useEffect(() => {
    dateRangeForm.setValue("dateRangeStart", dateRangeStart ?? "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    dateRangeForm.setValue("dateRangeEnd", dateRangeEnd ?? "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [dateRangeEnd, dateRangeForm, dateRangeStart]);
  useEffect(() => {
    if (selectedDateRange !== "CUSTOM_DATE_RANGE") {
      return;
    }
    const nextStart = watchedDateRangeStart.trim();
    const nextEnd = watchedDateRangeEnd.trim();
    if (!nextStart || !nextEnd) {
      return;
    }
    if (nextStart === dateRangeStart && nextEnd === dateRangeEnd) {
      return;
    }
    void navigate({
      search: (prev) => ({
        ...prev,
        dateRangeEnd: nextEnd,
        dateRangeStart: nextStart,
      }),
      replace: true,
    });
  }, [
    dateRangeEnd,
    dateRangeStart,
    navigate,
    selectedDateRange,
    watchedDateRangeEnd,
    watchedDateRangeStart,
  ]);

  const resolvedDateRange = useMemo(
    () =>
      resolveReportDateRange({
        dateRangeEnd,
        dateRangeStart,
        dateRangeType: selectedDateRange,
      }),
    [dateRangeEnd, dateRangeStart, selectedDateRange],
  );
  const effectiveDateRangeType = useMemo<ReportDateRangeType>(
    () =>
      selectedDateRange === "CUSTOM_DATE_RANGE" &&
      (!resolvedDateRange.start || !resolvedDateRange.end)
        ? DEFAULT_DATE_RANGE_TYPE
        : selectedDateRange,
    [resolvedDateRange.end, resolvedDateRange.start, selectedDateRange],
  );
  const hasCustomDateRange =
    effectiveDateRangeType === "CUSTOM_DATE_RANGE" &&
    Boolean(resolvedDateRange.start && resolvedDateRange.end);
  const hasNonDefaultDateRange =
    effectiveDateRangeType !== DEFAULT_DATE_RANGE_TYPE && !hasCustomDateRange;
  const hasVisibleDateRangeChip = hasCustomDateRange || hasNonDefaultDateRange;

  const { data: usersData } = useQuery({
    ...getUsersOptions(),
    enabled: canViewReport,
  });
  const { data: matterSettingsData } = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    enabled: canViewReport,
  });

  const reportFilters = useMemo(() => {
    return {
      dateRange: {
        preset: effectiveDateRangeType,
        ...(effectiveDateRangeType === "CUSTOM_DATE_RANGE"
          ? {
              endDate: resolvedDateRange.end,
              startDate: resolvedDateRange.start,
            }
          : {}),
      },
      filters: {
        assigneeUserIds: selectedAssignees,
        incidentReportStatuses: [],
        matterStatusLabels: selectedMatterStatuses,
        incidentTypeLabels: selectedLegacyIncidentTypeLabels,
        incidentTypeIds: selectedIncidentTypeIds,
        claimTypeIds: selectedClaimTypeIds,
        medicalTreatmentBillRecordStatuses: [],
        medicalTreatmentRecordStatuses: selectedMedicalTreatmentRecordStatuses,
        teamLabels: selectedTeams,
      },
    };
  }, [
    effectiveDateRangeType,
    resolvedDateRange.end,
    resolvedDateRange.start,
    selectedAssignees,
    selectedMatterStatuses,
    selectedMedicalTreatmentRecordStatuses,
    selectedTeams,
    selectedLegacyIncidentTypeLabels,
    selectedIncidentTypeIds,
    selectedClaimTypeIds,
  ]);

  const { data, error, isLoading } = useQuery({
    ...retrieveReportsMatterRecordRequestsReportQueryOptions(reportFilters),
    enabled: canViewReport,
  });

  const reports = useMemo<ReportsMatterRecordRequestsReport[]>(
    () => data?.data ?? [],
    [data?.data],
  );

  const teamOptions = useMemo(() => {
    const settingLabels = (matterSettingsData?.data.teams ?? []).map(
      (team) => team.label,
    );
    const reportLabels = reports.flatMap(
      (item) => item.matterRecordRequestsReportMatterTeams ?? [],
    );
    const uniqueLabels = getUniqueTrimmedValues([
      ...settingLabels,
      ...reportLabels,
      ...selectedTeams,
    ]);

    return uniqueLabels.map((label) => ({ value: label, label }));
  }, [matterSettingsData?.data.teams, reports, selectedTeams]);

  const userDirectory = useUserDirectory({
    enabled: canViewReport,
    selectedIds: selectedAssignees,
  });

  const assigneeOptions = useMemo(() => {
    const users = userDirectory.users;
    const mappedUsers = users
      .map((user) => {
        const label = [user.firstName.trim(), user.lastName.trim()]
          .filter(Boolean)
          .join(" ");

        return {
          value: user.id,
          label: label || user.id,
        };
      })
      .sort((left, right) => left.label.localeCompare(right.label));

    const existing = new Set(mappedUsers.map((option) => option.value));
    const options = [...mappedUsers];
    selectedAssignees.forEach((value) => {
      if (!existing.has(value)) {
        options.push({ value, label: value });
      }
    });

    return options;
  }, [selectedAssignees, userDirectory.users]);

  const userLabelById = useMemo(
    () =>
      new Map(
        (usersData?.data ?? []).map(
          (user) =>
            [
              user.id,
              `${user.firstName} ${user.lastName}`.trim() || user.id,
            ] as const,
        ),
      ),
    [usersData?.data],
  );

  const matterStatusOptions = useMemo(
    () =>
      buildMatterStatusLabelOptions(
        matterSettingsData?.data.statuses,
        selectedMatterStatuses,
      ),
    [matterSettingsData?.data.statuses, selectedMatterStatuses],
  );

  const medicalTreatmentRecordStatusOptions = useMemo(() => {
    const values = getUniqueTrimmedValues([
      ...MEDICAL_TREATMENT_RECORD_STATUS_OPTIONS,
      ...selectedMedicalTreatmentRecordStatuses,
    ]);

    return values.map((value) => ({ value, label: value }));
  }, [selectedMedicalTreatmentRecordStatuses]);

  const classificationFilterChips = classificationFilters.map((filter) => ({
    key: `${filter.dimension}:${filter.value}`,
    label: filter.label,
    onRemove: () => {
      const selected =
        filter.dimension === "incidentType"
          ? selectedIncidentTypeIds
          : selectedClaimTypeIds;
      void navigate({
        search: (prev) => ({
          ...prev,
          [filter.dimension]: serializeSearchValues(
            selected.filter((value) => value !== filter.value),
          ),
        }),
        replace: true,
      });
    },
  }));
  const appliedFilterChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      onRemove: () => void;
    }> = [];

    if (hasVisibleDateRangeChip) {
      const label =
        formatResolvedReportDateRangeLabel({
          dateRangeEnd: resolvedDateRange.end,
          dateRangeStart: resolvedDateRange.start,
          dateRangeType: effectiveDateRangeType,
        }) ?? effectiveDateRangeType;
      chips.push({
        key: `dateRange:${effectiveDateRangeType}:${resolvedDateRange.start ?? ""}:${resolvedDateRange.end ?? ""}`,
        label: `Date range: ${label}`,
        onRemove: () => {
          void navigate({
            search: (prev) => ({
              ...prev,
              dateRangeEnd: undefined,
              dateRangeStart: undefined,
              dateRangeType: DEFAULT_DATE_RANGE_TYPE,
            }),
            replace: true,
          });
        },
      });
    }

    selectedTeams.forEach((value) => {
      chips.push({
        key: `team:${value}`,
        label: `Team: ${resolveOptionLabel(teamOptions, value)}`,
        onRemove: () => {
          void navigate({
            search: (prev) => ({
              ...prev,
              team: serializeSearchValues(
                selectedTeams.filter((item) => item !== value),
              ),
            }),
            replace: true,
          });
        },
      });
    });

    selectedAssignees.forEach((value) => {
      chips.push({
        key: `assignee:${value}`,
        label: `Assignee: ${resolveOptionLabel(assigneeOptions, value)}`,
        onRemove: () => {
          void navigate({
            search: (prev) => ({
              ...prev,
              assignee: serializeSearchValues(
                selectedAssignees.filter((item) => item !== value),
              ),
            }),
            replace: true,
          });
        },
      });
    });

    selectedMatterStatuses.forEach((value) => {
      chips.push({
        key: `matterStatus:${value}`,
        label: `Matter Status: ${resolveOptionLabel(matterStatusOptions, value)}`,
        onRemove: () => {
          void navigate({
            search: (prev) => ({
              ...prev,
              matterStatus: serializeSearchValues(
                selectedMatterStatuses.filter((item) => item !== value),
              ),
            }),
            replace: true,
          });
        },
      });
    });

    selectedLegacyIncidentTypeLabels.forEach((value) => {
      chips.push({
        key: `type:${value}`,
        label: `Incident Type contains: ${value}`,
        onRemove: () => {
          void navigate({
            search: (prev) => ({
              ...prev,
              type: serializeSearchValues(
                selectedLegacyIncidentTypeLabels.filter(
                  (item) => item !== value,
                ),
              ),
            }),
            replace: true,
          });
        },
      });
    });

    selectedMedicalTreatmentRecordStatuses.forEach((value) => {
      chips.push({
        key: `medicalTreatmentRecordStatus:${value}`,
        label: `Medical record status: ${resolveOptionLabel(medicalTreatmentRecordStatusOptions, value)}`,
        onRemove: () => {
          void navigate({
            search: (prev) => ({
              ...prev,
              medicalTreatmentRecordStatus: serializeSearchValues(
                selectedMedicalTreatmentRecordStatuses.filter(
                  (item) => item !== value,
                ),
              ),
            }),
            replace: true,
          });
        },
      });
    });

    return chips;
  }, [
    assigneeOptions,
    effectiveDateRangeType,
    hasVisibleDateRangeChip,
    matterStatusOptions,
    navigate,
    resolvedDateRange.end,
    resolvedDateRange.start,
    selectedAssignees,
    selectedMatterStatuses,
    selectedMedicalTreatmentRecordStatuses,
    selectedTeams,
    selectedLegacyIncidentTypeLabels,
    teamOptions,
    medicalTreatmentRecordStatusOptions,
  ]);

  const columns = useMemo<ColumnDef<ReportsMatterRecordRequestsReport>[]>(
    () => [
      {
        accessorKey: "matterRecordRequestsReportMatterTitle",
        id: "Matter",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) =>
          row.original.matterRecordRequestsReportMatterTitle ?? "-",
        enableHiding: false,
      },
      {
        accessorKey: "matterRecordRequestsReportMatterAssigneeUserIds",
        id: "Assignees",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => {
          const labels = (
            row.original.matterRecordRequestsReportMatterAssigneeUserIds ?? []
          ).map((value) => userLabelById.get(value) ?? value);
          return <MultiValueCell value={labels} />;
        },
      },
      {
        accessorKey: "matterRecordRequestsReportMatterTeams",
        id: "Team",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <MultiValueCell
            value={row.original.matterRecordRequestsReportMatterTeams ?? []}
          />
        ),
      },
      {
        accessorKey: "matterRecordRequestsReportMatterStatusLabel",
        id: "Matter Status",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) =>
          row.original.matterRecordRequestsReportMatterStatusLabel ?? "-",
      },
      {
        accessorKey: "matterRecordRequestsReportIncidentType",
        id: "Incident Type",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) =>
          row.original.matterRecordRequestsReportIncidentType ?? "-",
      },
      {
        accessorFn: (row) =>
          (row.claimTypes ?? []).map((claimType) => claimType.label).join(", "),
        id: "Claim Types",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) => (
          <ClaimTypesCell claimTypes={row.original.claimTypes} />
        ),
      },
      {
        accessorKey: "matterRecordRequestsReportRequestKind",
        id: "Request Type",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) =>
          row.original.matterRecordRequestsReportRequestKind ?? "-",
      },
      {
        accessorKey: "matterRecordRequestsReportRequestLabel",
        id: "Request",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) =>
          row.original.matterRecordRequestsReportRequestLabel ?? "-",
      },
      {
        accessorKey: "matterRecordRequestsReportRequestDate",
        id: "Request Date",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        sortingFn: "customDateTime",
        cell: ({ row }) =>
          formatDateValue(row.original.matterRecordRequestsReportRequestDate),
      },
      {
        accessorKey: "matterRecordRequestsReportReceivedDate",
        id: "Received Date",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        sortingFn: "customDateTime",
        cell: ({ row }) =>
          formatDateValue(row.original.matterRecordRequestsReportReceivedDate),
      },
      {
        accessorKey: "matterRecordRequestsReportRejectedDate",
        id: "Rejected Date",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        sortingFn: "customDateTime",
        cell: ({ row }) =>
          formatDateValue(row.original.matterRecordRequestsReportRejectedDate),
      },
      {
        accessorKey: "matterRecordRequestsReportRequestStatus",
        id: "Request Status",
        header: ({ column }) => <DataTableColumnHeader column={column} />,
        cell: ({ row }) =>
          row.original.matterRecordRequestsReportRequestStatus ?? "-",
      },
    ],
    [userLabelById],
  );

  const getCsvValue = (
    row: ReportsMatterRecordRequestsReport,
    columnId: string,
  ) => {
    switch (columnId) {
      case "Claim Types":
        return (
          (row.claimTypes ?? [])
            .map((claimType) => claimType.label)
            .join(", ") || "-"
        );
      case "Matter":
        return row.matterRecordRequestsReportMatterTitle ?? "-";
      case "Assignees":
        return (row.matterRecordRequestsReportMatterAssigneeUserIds ?? [])
          .map((value) => userLabelById.get(value) ?? value)
          .join(", ");
      case "Team":
        return (row.matterRecordRequestsReportMatterTeams ?? []).join(", ");
      case "Matter Status":
        return row.matterRecordRequestsReportMatterStatusLabel ?? "-";
      case "Incident Type":
        return row.matterRecordRequestsReportIncidentType ?? "-";
      case "Request Type":
        return row.matterRecordRequestsReportRequestKind ?? "-";
      case "Request":
        return row.matterRecordRequestsReportRequestLabel ?? "-";
      case "Request Date":
        return formatDateValue(row.matterRecordRequestsReportRequestDate);
      case "Received Date":
        return formatDateValue(row.matterRecordRequestsReportReceivedDate);
      case "Rejected Date":
        return formatDateValue(row.matterRecordRequestsReportRejectedDate);
      case "Request Status":
        return row.matterRecordRequestsReportRequestStatus ?? "-";
      default:
        return "";
    }
  };

  if (!canViewReport) {
    return <GlobalNotAuthorized />;
  }

  return (
    <ReportsPageLayout title="Record requests report">
      <ReportFilterBar
        appliedFilters={[...appliedFilterChips, ...classificationFilterChips]}
        onClearFilters={async () => {
          dateRangeForm.reset(
            { dateRangeEnd: "", dateRangeStart: "" },
            { keepDefaultValues: true },
          );
          setTeamSelectValue(undefined);
          setMatterStatusSelectValue(undefined);
          setMedicalTreatmentRecordStatusSelectValue(undefined);
          await navigate({
            search: () => ({
              assignee: undefined,
              dateRangeEnd: undefined,
              dateRangeStart: undefined,
              dateRangeType: DEFAULT_DATE_RANGE_TYPE,
              medicalTreatmentRecordStatus: undefined,
              matterStatus: undefined,
              team: undefined,
              type: undefined,
              incidentType: undefined,
              claimType: undefined,
            }),
            replace: true,
          });
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            value={selectedDateRange}
            onValueChange={async (value) => {
              if (!isReportDateRangeType(value)) {
                return;
              }
              if (value === "CUSTOM_DATE_RANGE") {
                await navigate({
                  search: (prev) => ({
                    ...prev,
                    dateRangeEnd: undefined,
                    dateRangeStart: undefined,
                    dateRangeType: value,
                  }),
                  replace: true,
                });
                return;
              }

              await navigate({
                search: (prev) => ({
                  ...prev,
                  dateRangeEnd: undefined,
                  dateRangeStart: undefined,
                  dateRangeType: value,
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11 w-full sm:w-[220px]">
              <SelectValue placeholder="Select date range" />
            </SelectTrigger>
            <SelectContent>
              {REPORT_DATE_RANGE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedDateRange === "CUSTOM_DATE_RANGE" ? (
            <Form {...dateRangeForm}>
              <div className="flex flex-col gap-3 sm:flex-row">
                <DateFormField
                  clearable={false}
                  label="Start date"
                  name="dateRangeStart"
                  register={dateRangeForm.register("dateRangeStart")}
                />
                <DateFormField
                  clearable={false}
                  label="End date"
                  name="dateRangeEnd"
                  register={dateRangeForm.register("dateRangeEnd")}
                />
              </div>
            </Form>
          ) : null}
          <Select
            value={teamSelectValue ?? ""}
            onValueChange={async (value) => {
              setTeamSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  team: serializeSearchValues(
                    addUniqueValue(selectedTeams, value),
                  ),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11 w-full sm:w-[220px]">
              <SelectValue placeholder="Select team" />
            </SelectTrigger>
            <SelectContent>
              {teamOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ReportUserFilter
            {...userDirectory.selectProps}
            items={assigneeOptions}
            className="w-full sm:w-[220px]"
            placeholder="Select assignee"
            onChange={async (value) => {
              await navigate({
                search: (prev) => ({
                  ...prev,
                  assignee: serializeSearchValues(
                    addUniqueValue(selectedAssignees, value),
                  ),
                }),
                replace: true,
              });
            }}
          />
          <Select
            value={matterStatusSelectValue ?? ""}
            onValueChange={async (value) => {
              setMatterStatusSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  matterStatus: serializeSearchValues(
                    addUniqueValue(selectedMatterStatuses, value),
                  ),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11 w-full sm:w-[220px]">
              <SelectValue placeholder="Select matter status" />
            </SelectTrigger>
            <SelectContent>
              <MatterStatusSelectItems items={matterStatusOptions} />
            </SelectContent>
          </Select>
          <ClaimTypeFilter
            value={selectedClaimTypeIds}
            onChange={(value) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  claimType: serializeSearchValues(value),
                }),
                replace: true,
              });
            }}
          />
          <IncidentTypeFilter
            value={selectedIncidentTypeIds}
            onChange={(values) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  incidentType: serializeSearchValues(values),
                }),
                replace: true,
              });
            }}
          />
          <Select
            value={medicalTreatmentRecordStatusSelectValue ?? ""}
            onValueChange={async (value) => {
              setMedicalTreatmentRecordStatusSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  medicalTreatmentRecordStatus: serializeSearchValues(
                    addUniqueValue(
                      selectedMedicalTreatmentRecordStatuses,
                      value,
                    ),
                  ),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11 w-full min-w-0 sm:w-[220px]">
              <SelectValue placeholder="Medical record status" />
            </SelectTrigger>
            <SelectContent>
              {medicalTreatmentRecordStatusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </ReportFilterBar>
      <div className="space-y-4">
        <DataTable
          entityLabelPlural="record requests"
          columnIdAliases={REPORT_CLASSIFICATION_COLUMN_ALIASES}
          newColumnAnchors={REPORT_CLASSIFICATION_COLUMN_ANCHORS}
          hideSearchBar
          columns={columns}
          data={reports}
          renderRowContextMenu={(row, trigger) => {
            const matterId = row.original.matterRecordRequestsReportMatterId;
            return canViewMatterDetails && matterId ? (
              <MatterRowContextMenu matterId={matterId} trigger={trigger} />
            ) : (
              trigger
            );
          }}
          onRowClick={async (row) => {
            const matterId = row.original.matterRecordRequestsReportMatterId;
            if (!canViewMatterDetails || !matterId) {
              return;
            }
            await navigate({
              to: "/matters/$matterId",
              params: { matterId },
            });
          }}
          tableName={RECORD_REQUESTS_REPORT_TABLE_NAME}
          isLoading={isLoading}
          isError={Boolean(error)}
          noResultsMessage="No record requests found for the selected filters."
          leftToolbarContent={
            <Text variant="body" className="text-lg font-semibold">
              Record requests
            </Text>
          }
          exportCsv={{
            fileName: "record-requests-report.csv",
            getCellValue: getCsvValue,
          }}
        />
      </div>
    </ReportsPageLayout>
  );
}
